// src/ApiPanel/components/ApiPanelHeader.jsx
//
// What changed and why:
//
//   Not sticky            Reports here are long tables. The header scrolled
//                         away, taking the account menu and the sidebar
//                         toggle with it. It is sticky now.
//
//   Hardcoded ₹0.00       Balance / Hold / Sell Wallet were never wired to
//                         anything — `walletSummary` was an unset prop, so
//                         every account saw a zero balance forever. On a
//                         fintech panel a wrong number is worse than no
//                         number, so the strip now fetches real figures and
//                         renders nothing at all if it cannot get them.
//
//   Dead notification bell It did nothing. It now shows the count of open
//                         complaints and links to that screen — a real signal
//                         rather than furniture.
//
//   Duplicate brand       The sidebar already shows it. The header brand is
//                         now mobile-only, where the sidebar is off-canvas.
//
//   No page context       On a 50-item nav you lose track of where you are.
//                         The header shows the current section and page.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useApiPanelSession } from "../context/ApiPanelSessionContext";
import { API_PANEL_ENDPOINTS, API_PANEL_LOGIN_PATH } from "../constants/apiPanelAuth";
import { API_PANEL_NAV } from "../constants/apiPanelNav";
import { apiPanelFetch } from "../utils/apiPanelFetch";

const rupees = (n) =>
  `₹${Number(n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Walk the nav for the current path and return [section, page] labels. */
function findCrumbs(items, pathname, trail = []) {
  for (const item of items) {
    if (item.path && (pathname === item.path || pathname.startsWith(`${item.path}/`))) {
      return [...trail, item.label];
    }
    if (item.children?.length) {
      const hit = findCrumbs(item.children, pathname, [...trail, item.label]);
      if (hit) return hit;
    }
  }
  return null;
}

/**
 * Pull wallet figures without assuming a response shape.
 *
 * `api_partner_dashboard_api` returns a `wallets` block, but its exact keys
 * differ by role and I have not seen every variant — so this reads
 * defensively and shows only what it actually finds. Anything it cannot
 * identify is left out rather than displayed as zero.
 */
function useWalletSummary() {
  const [wallets, setWallets] = useState(null);

  useEffect(() => {
    let cancelled = false;
    apiPanelFetch(API_PANEL_ENDPOINTS.WALLET_SUMMARY)
      .then((res) => {
        if (cancelled) return;
        const w = res?.wallets ?? res?.data?.wallets;
        if (!w) return;
        const pick = (...names) => {
          for (const n of names) {
            const v = Array.isArray(w)
              ? w.find((x) => String(x?.name ?? "").toLowerCase().includes(n))?.balance
              : w[n];
            if (v !== undefined && v !== null && v !== "") return v;
          }
          return null;
        };
        const found = [
          ["Balance", pick("main", "main_wallet", "balance", "wallet_ammount")],
          ["Hold", pick("hold", "hold_wallet", "locked")],
          ["Sell Wallet", pick("sell", "sell_wallet", "aeps")],
        ].filter(([, v]) => v !== null && v !== undefined);
        setWallets(found.length ? found : null);
      })
      .catch(() => {
        // Not fatal — the panel works fine without a balance strip, and a
        // failed balance fetch must never block the page behind it.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return wallets;
}

/** Open complaints, for the bell. Silent on failure for the same reason. */
function useOpenComplaints() {
  const [openCount, setOpenCount] = useState(null);

  useEffect(() => {
    let cancelled = false;
    apiPanelFetch(API_PANEL_ENDPOINTS.TICKET_ACTION, {
      method: "POST",
      form: { action: "GET" },
    })
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.data) ? res.data : [];
        setOpenCount(
          rows.filter((t) => !/resolved|closed/i.test(String(t.status ?? ""))).length
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return openCount;
}

export default function ApiPanelHeader({ onToggleSidebar, onOpenMobileNav }) {
  const { user, logout } = useApiPanelSession();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const wallets = useWalletSummary();
  const openComplaints = useOpenComplaints();
  const crumbs = findCrumbs(API_PANEL_NAV, pathname) || [];

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setMenuOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  // Close the menu on navigation — otherwise it hangs open over the new page.
  useEffect(() => setMenuOpen(false), [pathname]);

  const handleLogout = useCallback(
    async (scope) => {
      setMenuOpen(false);
      await logout(scope);
      navigate(API_PANEL_LOGIN_PATH, { replace: true });
    },
    [logout, navigate]
  );

  return (
    <header className="apip-header">
      <div className="apip-header-left">
        {/* Two buttons, one job each: collapse the rail on desktop, open the
            drawer on mobile. CSS shows exactly one at a time. */}
        <button
          className="apip-burger apip-burger-desktop"
          onClick={onToggleSidebar}
          aria-label="Collapse navigation"
          type="button"
        >
          <i className="iconoir-menu" />
        </button>
        <button
          className="apip-burger apip-burger-mobile"
          onClick={onOpenMobileNav}
          aria-label="Open navigation"
          type="button"
        >
          <i className="iconoir-menu" />
        </button>

        <div className="apip-brand apip-brand-mobile">
          <span className="apip-brand-mark">API</span>
          <span className="apip-brand-name">Panel</span>
        </div>

        {crumbs.length > 0 && (
          <nav className="apip-crumbs" aria-label="Breadcrumb">
            {crumbs.map((c, i) => (
              <span key={`${c}-${i}`} className={i === crumbs.length - 1 ? "current" : ""}>
                {c}
              </span>
            ))}
          </nav>
        )}
      </div>

      {wallets && (
        <div className="apip-header-mid">
          {wallets.map(([label, value], i) => (
            <div key={label} className={`apip-stat${i === 2 ? " apip-stat-hide-sm" : ""}`}>
              <span className="apip-stat-label">{label}</span>
              <span className="apip-stat-value">{rupees(value)}</span>
            </div>
          ))}
        </div>
      )}

      <div className="apip-header-right">
        <Link className="apip-help-btn" to="/api-panel/complaint">
          <i className="iconoir-lifebelt" /> <span>Need Help?</span>
        </Link>

        <Link
          className="apip-icon-btn apip-bell"
          to="/api-panel/complaint"
          aria-label={
            openComplaints ? `${openComplaints} open complaints` : "Complaints"
          }
          title={openComplaints ? `${openComplaints} open complaints` : "Complaints"}
        >
          <i className="iconoir-bell" />
          {openComplaints > 0 && (
            <span className="apip-bell-dot">{openComplaints > 9 ? "9+" : openComplaints}</span>
          )}
        </Link>

        <div className="apip-profile" ref={menuRef}>
          <button
            className="apip-icon-btn apip-avatar"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Account menu"
            aria-expanded={menuOpen}
            type="button"
          >
            <i className="iconoir-user-circle" />
          </button>
          {menuOpen && (
            <div className="apip-profile-menu">
              <div className="apip-profile-menu-header">
                <div className="apip-profile-name">{user?.name || "API User"}</div>
                {user?.role && <div className="apip-profile-role">{user.role}</div>}
                <div className="apip-profile-email">
                  {user?.company || user?.phone_masked || user?.email_masked || ""}
                </div>
              </div>
              <Link to="/api-panel/account">Account</Link>
              <Link to="/api-panel/tokens/manage">API Tokens</Link>
              <Link to="/api-panel/complaint">Complaints</Link>
              <button className="apip-logout-btn" onClick={() => handleLogout("this")} type="button">
                <i className="iconoir-log-out" /> Log Out
              </button>
              {/* Revokes every session on every device — the right move if
                  someone thinks their account is compromised. */}
              <button
                className="apip-logout-btn apip-logout-all"
                onClick={() => handleLogout("all")}
                type="button"
              >
                Sign out everywhere
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
